// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Box, IconButton, Typography, List, ListItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import Draggable from 'react-draggable';
import PropTypes from 'prop-types';

const portalContainerId = 'custom-error-section-dialog-portal';

const getAttributeLabel = (sectionData, sectionName, attributeName) => {
  const section = sectionData?.find((sec) => sec?.section_name === sectionName);
  if (section) {
    const attribute = section?.attributes?.find(
      (attr) => attr?.name === attributeName
    );
    if (attribute?.label) {
      return attribute?.label;
    }
  }
  return attributeName; // Fallback to attribute name if label not found
};

const getOrCreateContainer = () => {
  let container = document.getElementById(portalContainerId);
  if (!container) {
    container = document.createElement('div');
    container.id = portalContainerId;
    document.body.appendChild(container);
  }
  return container;
};

const DraggableErrorSectionDialog = ({
  open,
  onClose,
  errors,
  sectionData,
  title = 'Form Errors',
}) => {
  const [portalContainer, setPortalContainer] = useState(null);
  const nodeRef = useRef(null);

  useEffect(() => {
    const container = getOrCreateContainer();
    setPortalContainer(container);

    return () => {
      // Only remove if no children remain
      if (container && container.children.length === 0) {
        container.remove();
      }
    };
  }, []);

  if (!open || !portalContainer) {
    return null;
  }

  const content = (
    <Box
      component="section"
      aria-labelledby="draggable-error-dialog-title"
      className="draggable-error-dialog"
    >
      <Draggable handle=".dialog-title" nodeRef={nodeRef}>
        <Box
          className="dialog-box"
          ref={nodeRef}
          sx={{
            width: 'fit-content',
            boxShadow: 6,
            borderRadius: 1,
            overflow: 'hidden',
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1300,
            pointerEvents: 'auto',
          }}
        >
          <Box
            className="dialog-title"
            sx={{
              cursor: 'move',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 8px',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <WarningRoundedIcon />
              <Typography
                id="draggable-error-dialog-title"
                variant="body1"
                className="dialog-title-text"
              >
                {title} ({Object.keys(errors || {})?.length})
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              className="dialog-close-button"
              aria-label="Close dialog"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            sx={{ maxHeight: '400px', overflowY: 'auto' }}
            className="dialog-content"
          >
            <List sx={{ padding: 0 }}>
              {Object.keys(errors || {})?.map((section) => {
                return (
                  <ListItem
                    key={section}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      padding: '8px 8px',
                      alignItems: 'flex-start',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <ErrorRoundedIcon fontSize="small" color="error" />
                      <Typography variant="subtitle1">{section}</Typography>
                    </Box>

                    <List sx={{ padding: 0 }}>
                      {Object.keys(errors[section] || {})?.map(
                        (errorAttributeName) => {
                          const attributeLabel = getAttributeLabel(
                            sectionData,
                            section,
                            errorAttributeName
                          );
                          return (
                            <ListItem
                              key={errorAttributeName}
                              sx={{ padding: '0px 0px 8px 25px' }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ wordBreak: 'break-all' }}
                              >
                                {attributeLabel}
                              </Typography>
                            </ListItem>
                          );
                        }
                      )}
                    </List>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>
      </Draggable>
    </Box>
  );

  return ReactDOM.createPortal(content, portalContainer);
};

DraggableErrorSectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.objectOf(PropTypes.string)).isRequired,
  sectionData: PropTypes.arrayOf(
    PropTypes.shape({
      section_name: PropTypes.string.isRequired,
      attributes: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  title: PropTypes.string,
};

export default DraggableErrorSectionDialog;
